// src/pages/EmployeeDashboard.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import { useAuth } from '../context/AuthContext';
import { getTasks, updateTask } from '../services/mockApi';
import { Task } from '../types';
import { Container, FlexCenter } from '../styles/commonStyles';

const DashboardWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background}; 
`;

const Sidebar = styled.nav<{ isOpen: boolean }>`
  width: 250px;
  background-color: ${({ theme }) => theme.colors.primary}; // Teal, matches LoginPage
  color: white;
  padding: ${({ theme }) => theme.spacing.large};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  height: 100vh;
  z-index: 1000;
  transition: transform 0.3s ease-in-out;

  position: fixed;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 200px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: sticky;
    top: 0;
    transform: translateX(0);
    align-self: start;
  }
`;

const SidebarToggle = styled.button`
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1100;
  background-color: ${({ theme }) => theme.colors.primary}; // Teal
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const SidebarItem = styled.div<{ active: boolean }>`
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ active, theme }) =>
    active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MainContent = styled(Container)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.large};
  background-color: ${({ theme }) => theme.colors.background}; 
  margin-left: 250px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
    padding: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary}; // Teal, matches LoginPage
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.6rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.4rem;
  }
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
  background: white; // Matches LoginPage form background
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.3rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

const TaskCard = styled.div<{ isOverdue?: boolean; isDueSoon?: boolean }>`
  background: white;
  padding: ${({ theme }) => theme.spacing.medium};
  color: ${({ theme }) => theme.colors.primary};

  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme, isOverdue, isDueSoon }) => 
    isOverdue ? theme.colors.danger : isDueSoon ? theme.colors.warning : theme.colors.muted}; // Matches LoginPage input borders
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const TaskDetails = styled.div`
  margin-top: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.muted};
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.muted}; 
  border-radius: 5px;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary}; // Teal, matches LoginPage
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 0.9rem;
    padding: 6px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 5px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const FilterSelect = styled(Select)`
  width: 150px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const NotificationBanner = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: ${({ theme }) => theme.colors.primary}; // Teal, matches LoginPage
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1000;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    top: 60px;
    right: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
`;

const EmployeeDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [completedTasks, setCompletedTasks] = useState<Task[]>([]);
  const [notifications, setNotifications] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTasks = async () => {
      const fetchedTasks: Task[] = await getTasks();
      // Filter tasks assigned to the current employee
    const employeeTasks = fetchedTasks.filter((task) => task.assignedTo === user?.id);

    //separate active and completed tasks
    const activeTasks = employeeTasks.filter((task) => task.status !== 'completed'); 
    const completed = employeeTasks.filter((task) => task.status === 'completed');
    setTasks(activeTasks);
    setCompletedTasks(completed);
};
fetchTasks();
  }, [user]);


  const addNotification = (message: string) => {
    setNotifications((prev) => [...prev, message]);
  };

  const removeNotification = (index: number) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: Task['status']) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const updatedTask = await updateTask(taskId, { ...task, status: newStatus });
    if (newStatus === 'completed') {
      // Remove from active tasks and add to completed tasks
      setTasks(tasks.filter((t) => t.id !== taskId));
      setCompletedTasks((prev) => [...prev, updatedTask]);
      addNotification(`Task "${updatedTask.title}" completed successifully and removed`);
    } else {
      setTasks(tasks.map((t) => (t.id === taskId ? updatedTask : t)));
      addNotification(`Task "${updatedTask.title}" updated to "${newStatus}".`);
    }
      setSelectedTask(null);
  };

  const toggleTaskDetails = (taskId: string) => {
    setSelectedTask(selectedTask === taskId ? null : taskId);
  };

  const filteredTasks = tasks.filter((task) =>
    filterStatus === 'all' ? true : task.status === filterStatus
  );

  const isTaskOverdue = (dueDate: string) => {
    try {
      return new Date(dueDate) < new Date();
    } catch (error) {
      return false;
    }
  };

  const isTaskDueSoon = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffInHours = (due.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diffInHours <= 24 && diffInHours > 0; // Due within 24 hours
  };


  const handleLogout = () => {
    logout();
    navigate ('/login');
    setIsSidebarOpen(false);
  }

  return (
    <DashboardWrapper>
      <SidebarToggle onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
        {isSidebarOpen ? '✕' : '☰'}
      </SidebarToggle>

      <Sidebar isOpen={isSidebarOpen}>
        <Title style={{ color: 'white', fontSize: '1.8rem' }}>Employee Portal</Title>
        <SidebarItem
          active={activeTab === 'tasks'}
          onClick={() => {
            setActiveTab('tasks');
            setIsSidebarOpen(false);
          }}
        >
          Tasks
        </SidebarItem>
        <SidebarItem
          active={activeTab === 'history'}
          onClick={() => {
            setActiveTab('history');
            setIsSidebarOpen(false);
          }}
        >
          Task History
        </SidebarItem>
        <SidebarItem
          active={false}
          onClick= { handleLogout }
        >
          Log Out
        </SidebarItem>
      </Sidebar>

      <MainContent>
        {/* <Title>Employee Dashboard </Title> */}
          {user && <SectionTitle>Welcome, {user.username}</SectionTitle>}

        {notifications.map((notification, index) => (
          <NotificationBanner key={index}>
            <span>{notification}</span>
            <CloseButton onClick={() => removeNotification(index)}>×</CloseButton>
          </NotificationBanner>
        ))}

        {activeTab === 'tasks' && (
          <Section>
            <SectionTitle>Your Tasks</SectionTitle>
            <FilterContainer>
              <FilterSelect
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
              </FilterSelect>
            </FilterContainer>
            {filteredTasks.length === 0 ? (
              <p style={{ color: theme.colors.primary }}>
                No tasks match your filter.
              </p>
            ) : (
              filteredTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  onClick={() => toggleTaskDetails(task.id)}
                  isOverdue={task.status !== 'completed' && isTaskOverdue(task.dueDate)}
                  isDueSoon={task.status !== 'completed' && isTaskDueSoon(task.dueDate)}
                >
                  <div style={{ flex: 1 }}>
                    <strong>{task.title}</strong> - {task.description}
                    <div>Status: {task.status}</div>
                    {task.status !== 'completed' && (
                      <div>
                        {isTaskOverdue(task.dueDate) && (
                          <span style={{ color: theme.colors.danger, marginLeft: '10px' }}>
                            Overdue
                          </span>
                        )}
                        {isTaskDueSoon(task.dueDate) && !isTaskOverdue(task.dueDate) && (
                          <span style={{ color: theme.colors.warning, marginLeft: '10px' }}>
                            Due Soon
                          </span>
                        )}
                      </div>
                    )}
                    {selectedTask === task.id && (
                      <TaskDetails>
                        <p><strong>Due Date:</strong> {task.dueDate}</p>
                        <p><strong>Assigned By:</strong> {task.assignedBy}</p>
                        <p><strong>Details:</strong> {task.description}</p>
                      </TaskDetails>
                    )}
                  </div>
                  <Select
                    value={task.status}
                    onChange={(e) =>
                      handleUpdateTaskStatus(task.id, e.target.value as Task['status'])
                    }
                    onClick={(e) => e.stopPropagation()} // Prevent toggling details when clicking the dropdown
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </Select>
                </TaskCard>
              ))
            )}
          </Section>
        )}

        {activeTab === 'history' && (
          <Section>
            <SectionTitle>Completed Tasks</SectionTitle>
            {completedTasks.length === 0 ? (
              <p style={{ color: theme.colors.text }}>
                No completed tasks yet.
              </p>
            ) : (
              completedTasks.map((task) => (
                <TaskCard 
                key={task.id} 
                onClick={() => toggleTaskDetails(task.id)}
                >
                  <div style={{ flex: 1 }}>
                    <strong>{task.title}</strong> - {task.description}
                    <div>Status: {task.status}</div>
                    {selectedTask === task.id && (
                      <TaskDetails>
                        <p><strong>Due Date:</strong> {task.dueDate}</p>
                        <p><strong>Created By:</strong> {task.assignedBy}</p>
                        <p><strong>Details:</strong> {task.description}</p>
                      </TaskDetails>
                    )}
                  </div>
                </TaskCard>
              ))
            )}
          </Section>
        )}
      </MainContent>
    </DashboardWrapper>
  );
};

export default EmployeeDashboard;